import {useTranslation} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';
import {showToast} from '@chekinapp/ui';
import {
  markLeaderContactVerified,
  useVerifiedContacts,
  type VerifiedContactsPayload,
} from '@chekin/shared/guest-form';
import {useMutateReservation} from 'hooks';
import {useRemoteIVFlow} from 'hooks/iv/ivRemoteFlow';
import {useVerificationSettings} from 'hooks/iv/useVerificationSettings';
import {useReservation} from 'context/reservation';
import {ContactsVerificationComplete} from './ContactsVerificationComplete';
import {ContactsVerificationSkeleton} from './ContactsVerificationSkeleton';

const ContactsVerificationView = () => {
  const {t} = useTranslation();
  const {housing} = useReservation();
  const {isEmailEnabled, isPhoneEnabled, isNeedVerification, isLoading} =
    useVerificationSettings(housing?.id);
  const {goNextAfterContactVerification} = useRemoteIVFlow(isNeedVerification);
  const queryClient = useQueryClient();

  const {mutateAsync: mutateReservation} = useMutateReservation();

  const handleAllVerified = async (payload: VerifiedContactsPayload) => {
    try {
      await mutateReservation({payload});
    } catch {
      showToast.error(t('something_went_wrong'));
      return;
    }

    if (import.meta.env.DEV) {
      markLeaderContactVerified(queryClient);
    }

    goNextAfterContactVerification();
  };

  const handleVerificationComplete = useVerifiedContacts({
    isEmailEnabled,
    isPhoneEnabled,
    onAllVerified: handleAllVerified,
  });

  if (isLoading) return <ContactsVerificationSkeleton />;

  return (
    <ContactsVerificationComplete
      enableEmail={isEmailEnabled}
      enablePhone={isPhoneEnabled}
      onVerificationComplete={handleVerificationComplete}
    />
  );
};

export {ContactsVerificationView};
